/**
 * @file Mode Monster (pleinement jouable) : defense de vagues ; les monstres foncent vers une barriere centrale, la toucher retire des PV.
 */
import { Decimal } from '@shared/bignum';
import type { EntityId } from '@shared/types';
import type { ModeDef, TargetDef } from '@config/types';
import type { SimContext } from '../../Context';
import { stageHp } from '../../balance/formulas';
import { spawnTarget } from '../../ecs/EntityFactory';
import type { With } from '../../ecs/World';
import { BaseMode } from '../BaseMode';
import type { ModeExitReason, ModeHud, ModeResult, ModeStartParams } from '../ModeTypes';
import { layoutPositions, pickSpawnEntry } from '../spawnLayout';

/** Pause (s) entre deux vagues. */
const INTER_WAVE_SECONDS = 2;
/** Fraction de la duree d'une vague pendant laquelle les monstres sont emis. */
const EMIT_FRACTION = 0.6;
const BOSS_HP_MULT = 10;
const MONSTER_REWARD_MULT = 0.5;
const DIA_PER_WAVE_ON_WIN = 5;
const TIER_OFFSET = 10;

/**
 * Regles (design/GAME_DESIGN.md §Mode Monster, original Monster_manager) :
 *  - Barriere : cercle de rayon params.barrierRadius centre en (0,0), PV params.barrierHp (this.barrierHp/barrierMax).
 *    Les chats restent DANS la barriere (ControlSystem borne le chef a barrierRadius - 0.8).
 *  - Vague n (1-based) : count = round(monstersPerWaveBase * monstersPerWaveGrowth^(n-1)) monstres, emis au fil de
 *    params.waveSeconds (60 % de la duree) sur l'anneau de rayon arena.halfW - 0.5, type tire dans rules.spawn.table avec value = n.
 *    PV : stageHp(run.bestStageValue - 10) * hpPerWaveGrowth^(n-1) * def.hpMult ; argent : stageMoney(idem) * 0.5 * def.rewardMult.
 *  - Toutes les bossEveryWaves vagues : +1 'mon_tank' boss (isBoss, hp x10, scale x1.6). WaveStarted { boss }.
 *  - Vague terminee quand tous ses monstres sont morts ou ont touche la barriere -> WaveCleared, missions 'wavesCleared',
 *    gain { petCoin: petCoinPerWave } (x petCoinGain), pause de 2 s puis vague suivante. this.wave = numero courant.
 *  - Contact barriere (EnemyAiSystem 'chargeBarrier' -> MonsterMode.onBarrierContact) : barrierHp -= barrierDamage,
 *    BarrierDamaged, le monstre est detruit SANS recompense.
 *  - Victoire : wave > wavesPerRun ; defaite : barrierHp <= 0. Resultat : modeBests.monster = max(vagues repoussees).
 *  - knockback : params.knockback (applique par applyDamage sur les monstres touches).
 */
export class MonsterMode extends BaseMode<'monster'> {
  private toSpawn = 0;
  private spawnAcc = 0;
  private spawnInterval = 1;
  private waveActive = false;
  private pauseLeft = 0;
  private readonly alive = new Set<EntityId>();
  private petCoins = 0;

  constructor(def: ModeDef<'monster'>) {
    super(def);
  }

  override get knockback(): number {
    return this.params.knockback;
  }

  get barrierRadius(): number {
    return this.params.barrierRadius;
  }

  protected override onEnter(ctx: SimContext, _params: ModeStartParams): void {
    this.barrierMax = this.params.barrierHp;
    this.barrierHp = this.barrierMax;
    this.wave = 0;
    this.toSpawn = 0;
    this.spawnAcc = 0;
    this.waveActive = false;
    this.pauseLeft = 0;
    this.petCoins = 0;
    this.alive.clear();
    ctx.rt.biome = 'forest';
    ctx.services.team.placeFormation(0, 0);
  }

  protected override onStart(ctx: SimContext): void {
    this.startWave(ctx, 1);
  }

  private startWave(ctx: SimContext, n: number): void {
    const p = this.params;
    this.wave = n;
    const count = Math.max(1, Math.round(p.monstersPerWaveBase * Math.pow(p.monstersPerWaveGrowth, n - 1)));
    this.toSpawn = count;
    this.spawnAcc = 0;
    this.spawnInterval = Math.max(0.02, (p.waveSeconds * EMIT_FRACTION) / count);
    this.waveActive = true;
    const boss = p.bossEveryWaves > 0 && n % p.bossEveryWaves === 0;
    ctx.bus.emit('WaveStarted', { wave: n, boss });
    if (boss) this.spawnMonster(ctx, ctx.index.target('mon_tank'), true);
  }

  private spawnMonster(ctx: SimContext, def: TargetDef, boss: boolean): void {
    // Base sur la frontiere de la RUN (remise a zero par un Rebirth), 10 niveaux en dessous : les monstres restent battables.
    const tier = Math.max(0, ctx.data.run.bestStageValue - TIER_OFFSET);
    const growth = Math.pow(this.params.hpPerWaveGrowth, this.wave - 1);
    const hp = stageHp(ctx.config.balance, tier).times(growth * def.hpMult * (boss ? BOSS_HP_MULT : 1));
    const [pos] = layoutPositions(ctx.rng.spawn, {
      layout: 'ring',
      arena: this.def.rules.arena,
      count: 1,
      minSpacing: 0,
      avoid: [],
      ringRadius: this.def.rules.arena.halfW - 0.5,
    });
    if (!pos) return;
    const e = spawnTarget(ctx, def, pos.x, pos.y, { stageValue: tier, hpOverride: hp, rewardMult: MONSTER_REWARD_MULT, isBoss: boss });
    this.alive.add(e.id);
    this.spawnedCount++;
  }

  protected override onRunning(ctx: SimContext, dt: number): void {
    if (this.pauseLeft > 0) {
      this.pauseLeft -= dt;
      if (this.pauseLeft <= 0) this.startWave(ctx, this.wave + 1);
      return;
    }
    if (!this.waveActive) return;
    const sp = this.def.rules.spawn;
    if (this.toSpawn > 0) {
      this.spawnAcc += dt;
      while (this.spawnAcc >= this.spawnInterval && this.toSpawn > 0) {
        if (ctx.rt.world.count('target') >= sp.maxAlive) {
          this.spawnAcc = this.spawnInterval; // attend qu'une place se libere
          break;
        }
        this.spawnAcc -= this.spawnInterval;
        const entry = pickSpawnEntry(ctx.rng.spawn, sp.table === 'biome' ? [] : sp.table, this.wave);
        if (!entry) {
          this.toSpawn = 0;
          break;
        }
        this.spawnMonster(ctx, ctx.index.target(entry.targetId), false);
        this.toSpawn--;
      }
    }
    if (this.toSpawn === 0 && this.alive.size === 0) this.finishWave(ctx);
  }

  private finishWave(ctx: SimContext): void {
    this.waveActive = false;
    const cleared = this.wave;
    ctx.bus.emit('WaveCleared', { wave: cleared });
    ctx.services.missions.increment('wavesCleared', 1);
    const credited = ctx.tx.earn({ petCoin: new Decimal(this.params.petCoinPerWave) }, 'wave');
    this.petCoins += credited.petCoin?.toNumber() ?? 0;
    this.recordBest(ctx, cleared);
    if (cleared >= this.params.wavesPerRun) this.wave = cleared + 1; // victoire (wave > wavesPerRun)
    else this.pauseLeft = INTER_WAVE_SECONDS;
  }

  protected override onKill(ctx: SimContext, target: With<'target' | 'health'>): void {
    this.alive.delete(target.id);
    const ai = target.ai;
    if (ai?.behavior === 'bomberBlast') {
      ctx.bus.emit('Explosion', { x: target.transform.x, y: target.transform.y, radius: ai.params.blastRadius ?? 2.5, behavior: 'explosive' });
    }
  }

  /** Appele par EnemyAiSystem quand un monstre touche la barriere. */
  onBarrierContact(ctx: SimContext, monster: With<'target'>): void {
    if (!this.alive.delete(monster.id)) return;
    const amount = monster.target.barrierDamage;
    this.barrierHp = Math.max(0, this.barrierHp - amount);
    ctx.bus.emit('BarrierDamaged', { amount, hp: this.barrierHp, max: this.barrierMax });
    ctx.rt.targetGrid.remove(monster.id);
    ctx.rt.world.destroy(monster.id);
  }

  private recordBest(ctx: SimContext, wavesRepelled: number): void {
    const bests = ctx.data.progress.modeBests;
    bests.monster = Math.max(bests.monster ?? 0, wavesRepelled);
  }

  private buildResult(ctx: SimContext, success: boolean): ModeResult {
    const repelled = success ? this.params.wavesPerRun : Math.max(0, this.wave - 1);
    this.recordBest(ctx, repelled);
    const dia = success ? DIA_PER_WAVE_ON_WIN * repelled : 0;
    return {
      mode: 'monster',
      success,
      score: repelled,
      wave: repelled,
      rewards: { wallet: { dia, petCoin: this.petCoins } },
    };
  }

  protected override onWin(ctx: SimContext): void {
    this.lastResult = this.buildResult(ctx, true);
    ctx.tx.grantBundle({ wallet: { dia: this.lastResult.rewards.wallet?.dia ?? 0 } }, 'mode:monster');
  }

  protected override onLose(ctx: SimContext): void {
    this.lastResult = this.buildResult(ctx, false);
  }

  protected override onExit(ctx: SimContext, _reason: ModeExitReason): ModeResult | null {
    if (!this.lastResult && this.wave > 1) this.recordBest(ctx, this.wave - 1);
    this.clearArena(ctx);
    this.alive.clear();
    return this.lastResult;
  }

  protected override hudExtras(_ctx: SimContext): Partial<ModeHud> {
    return { titleKey: 'hud.wave', titleParams: { wave: this.wave, max: this.params.wavesPerRun }, progress: { current: Math.max(0, this.wave - 1), max: this.params.wavesPerRun } };
  }
}
